"use client";

import React, { useEffect, useRef } from "react";
import { PageCareerSchema } from "@/sanity/pages/PageCareerTemplate";
import { cn } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { PortableTextBlock } from "sanity";
import { richTextComponent } from "@/sanity/sanityPortableText";
import ChipSerif from "@/components/global/ui/ChipSerif";
import Image from "next/image";
import { motion, useInView } from "framer-motion";
import { GROQImage } from "@/sanity/lib/definitions";
import logo from "@/public/career/logo.svg";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { useEffectMemo, useResizeMemo } from "@/helpers/utilFunctions";

gsap.registerPlugin(ScrollTrigger);

interface CardProps {
  item: {
    image?: GROQImage;
    background?: GROQImage;
    title?: string;
    description?: string;
  };
  index: number;
  isLast?: boolean;
}

const cardStyles = [
  {
    // First Card
    bg: "bg-[#93BCDB]",
    overlayGradient: "from-[#93BCDB] via-[#93BCDB] lg:via-[#93BCDB]/50 to-transparent",
    imageClasses:
      "!w-[30.125rem] lg:!w-[35rem] bottom-0 left-[50%] lg:left-auto -translate-x-1/2 lg:-translate-x-0 lg:right-0",
    descriptionWidth: "w-full lg:w-[min(36rem,100%)]",
  },
  {
    // Second Card
    bg: "bg-[#85D0A6]",
    overlayGradient: "from-[#85D0A6] via-[#85D0A6] lg:via-[#85D0A6]/50 to-transparent",
    imageClasses:
      "!w-[20rem] lg:!w-[26.75rem] lg:top-0 lg:bottom-auto bottom-[1.5rem] left-[50%] lg:left-auto -translate-x-1/2 lg:-translate-x-0 lg:right-[4rem]",
    descriptionWidth: "w-full lg:w-[min(35.25rem,100%)]",
  },
  {
    // Third Card
    bg: "bg-[#FFF7E2]",
    overlayGradient: "from-[#FFF7E2] via-[#FFF7E2] lg:via-[#FFF7E2]/50 to-transparent",
    imageClasses:
      "!w-[25rem] lg:!w-[30rem] bottom-0 left-[50%] lg:left-auto -translate-x-1/2 lg:-translate-x-0 lg:right-[3rem]",
    descriptionWidth: "w-full lg:w-[min(40rem,100%)]",
  },
  {
    // Fourth Card
    bg: "bg-[#93BCDB]",
    overlayGradient: "from-[#93BCDB] via-[#93BCDB] lg:via-[#93BCDB]/50 to-transparent",
    imageClasses:
      "!w-[23rem] lg:!w-[27rem] top-auto bottom-[1.5rem] lg:bottom-auto lg:top-1/2 left-[50%] lg:left-auto -translate-x-1/2 lg:-translate-x-0 lg:right-[3rem] lg:-translate-y-1/2",
    descriptionWidth: "w-full lg:w-[min(45rem,100%)]",
  },
];

function CardContent({
  item,
  currentStyle,
  isLast = false,
}: {
  item: CardProps["item"];
  currentStyle: (typeof cardStyles)[0];
  isLast?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex relative flex-col lg:flex-row items-center justify-between w-full rounded-[3rem] overflow-hidden lg:h-[35rem]",
        isLast ? "h-[42rem]" : "h-[53rem]",
        currentStyle.bg,
      )}>
      <div
        className={cn(
          "absolute w-full lg:w-[37.15rem] h-[40rem] lg:h-full left-0 lg:left-auto  top-0 right-0 lg:bottom-0 bg-gradient-to-b lg:bg-gradient-to-r z-10",
          currentStyle.overlayGradient,
        )}
      />
      {/* Background Image */}
      {/* absolute !w-full lg:!w-[37rem] !h-auto lg:!h-full left-[2rem] lg:left-auto lg:top-0 lg:right-0 bottom-[-4rem] lg:bottom-0 scale-110 lg:scale-100 lg:opacity-100 opacity-50 */}
      {item.background && (
        <Image
          className="absolute !w-full lg:!w-[37rem] !h-auto lg:!h-full left-[2rem] lg:left-auto lg:top-0 lg:right-0 bottom-[-4rem] lg:bottom-0 scale-110 lg:scale-100 lg:opacity-100 opacity-50"
          src={item.background}
          alt="absolute"
        />
      )}

      {/* Main Image */}
      {item.image && (
        <Image
          className={cn("absolute z-10", currentStyle.imageClasses, isLast && "hidden lg:block")}
          src={item.image}
          alt={item.title || ""}
          quality={100}
        />
      )}

      {/* Content */}
      <div className="relative z-20 w-full h-full p-[3rem] lg:p-[4rem]">
        <div className="flex flex-col items-center w-full h-full lg:justify-between lg:items-start gap-[1.5rem]">
          <Image
            src={logo}
            alt="iqcapital logo"
            className="flex-shrink-0 w-[3.875rem] aspect-square"
          />
          <div className="flex flex-col items-center lg:items-start">
            {item.title && (
              <h3 className="font-instrument-sans font-[600] tracking-[-0.06rem] lg:tracking-[-0.12rem] leading-[125%] text-[3rem] text-[#103037] text-center lg:text-start">
                {item.title}
              </h3>
            )}

            {item.description && (
              <p
                className={cn(
                  "text-[2.25rem] font-geist lg:text-[2rem] text-[#07171B]/80 leading-[150%] mb-0 text-center lg:text-start",
                  currentStyle.descriptionWidth,
                )}>
                {item.description}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function CardMobile({ item, index, isLast = false }: CardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const currentStyle = cardStyles[index % cardStyles.length];

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 80, filter: "blur(12px)" }}
      animate={isInView ? { opacity: 1, y: 0, filter: "blur(0px)" } : { opacity: 0, y: 80, filter: "blur(12px)" }}
      transition={{
        duration: 0.3,
        delay: index * 0.1,
        ease: [0.25, 0.4, 0.25, 1],
      }}
      className={cn("w-[min(112.625rem,100%)] mx-auto relative ")}>
      <CardContent
        item={item}
        currentStyle={currentStyle}
        isLast={isLast}
      />
    </motion.div>
  );
}

function CardWeb({ cards }: { cards: CardProps["item"][] }) {
  const section = useRef<HTMLDivElement>(null);

  const itemsContainer = useEffectMemo<HTMLElement | null>(
    () => (section.current ? (section.current.querySelector("div[data-cards-items]") ?? null) : null),
    [section],
  );

  const items = useEffectMemo<HTMLElement[]>(() => {
    if (!itemsContainer.current) return [] as HTMLElement[];
    const items = itemsContainer.current.querySelectorAll("div[data-cards-item]") ?? null;
    return items ? ([...items] as HTMLElement[]) : ([] as HTMLElement[]);
  }, [itemsContainer]);

  const absoluteItems = useEffectMemo<HTMLElement[]>(() => {
    return (
      items.current?.filter((item) => {
        const index = item.dataset?.["cardsItem"];
        return index && index !== "0";
      }) ?? ([] as HTMLElement[])
    );
  }, [items]);

  const sectionContainer = useEffectMemo<HTMLElement | null>(
    () => section.current?.closest(".container") ?? null,
    [section],
  );

  const sectionPin = useEffectMemo<HTMLElement | null>(
    () => section.current?.querySelector("div[data-cards-pin]") ?? null,
    [section],
  );

  const sectionWrapper = useEffectMemo<HTMLElement | null>(
    () => sectionContainer.current?.closest("section") ?? null,
    [sectionContainer],
  );

  const sectionTitle = useEffectMemo<HTMLElement | null>(
    () => section.current?.querySelector("div[data-cards-title]") ?? null,
    [section],
  );

  const sectionContainerTop = useResizeMemo(() => {
    return sectionContainer.current?.getBoundingClientRect().top || 0;
  }, [sectionContainer]);

  const sectionWrapperTop = useResizeMemo(() => {
    return sectionWrapper.current?.getBoundingClientRect().top || 0;
  }, [sectionWrapper]);

  const pinTop = React.useMemo(() => {
    return (sectionContainerTop ?? 0) - (sectionWrapperTop ?? 0) || 0;
  }, [sectionContainerTop, sectionWrapperTop]);

  const sectionTitleHeight = useResizeMemo(() => {
    return sectionTitle.current?.getBoundingClientRect().height || 0;
  }, [sectionTitle]);

  const remSize = useResizeMemo(() => {
    return typeof window !== "undefined"
      ? parseFloat(window.getComputedStyle(document.documentElement).getPropertyValue("font-size")) || 8
      : 8;
  }, []);

  const gap = useResizeMemo(() => {
    return 3 * remSize;
  }, [remSize]);

  const itemHeights = useResizeMemo(() => {
    return (
      items.current?.map((item) => {
        const box = item.getBoundingClientRect();
        return box.height;
      }) ?? ([] as number[])
    );
  }, [items]);

  const itemTops = useResizeMemo(() => {
    return (
      items.current?.map((item) => {
        const index = item.dataset?.["cardsItem"];
        const i = index ? parseInt(index) : 0;
        const before = items.current?.slice(0, i) ?? [];

        return before.reduce((acc, item) => {
          const index = item.dataset?.["cardsItem"];
          const i = index ? parseInt(index) : 0;
          const height = itemHeights.length > i ? itemHeights[i] : 0;
          return acc + height + gap;
        }, 0);
      }) ?? ([] as number[])
    );
  }, [items, itemHeights]);

  const containerHeight = useResizeMemo(() => {
    const height =
      items.current?.reduce((acc, item) => {
        const index = item.dataset?.["cardsItem"];
        const i = index ? parseInt(index) : 0;
        const height = itemHeights[i];
        return acc + height;
      }, 0) ?? 0;

    const gapHeight = gap * ((items.current?.length || 1) - 1);
    return height + gapHeight;
  }, [items, itemHeights, gap]);

  const sectionContainerHeight = useResizeMemo(() => {
    return containerHeight + sectionTitleHeight + itemHeights[itemHeights.length - 1] * 0.3;
  }, [containerHeight, sectionTitleHeight, itemHeights]);

  useEffect(() => {
    if (sectionContainer.current) {
      sectionContainer.current.style.height = `${sectionContainerHeight}px`;
    }
  }, [sectionContainerHeight, sectionContainer]);

  useEffect(() => {
    if (sectionPin.current) {
      sectionPin.current.style.top = `${pinTop}px`;
    }
  }, [sectionPin, pinTop]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      if (absoluteItems) {
        const itemsTop = pinTop + sectionTitleHeight;
        const firstMiddle = itemTops[0] + itemHeights[0] * 0.25;
        absoluteItems.current?.forEach((item) => {
          const index = item.dataset?.["cardsItem"];
          if (index) {
            const i = parseInt(index);
            const reverseIndex = (items.current?.length || 0) - i;
            const scaleDown = 0.03 * (reverseIndex + 1);
            const scalePadding = 2.8 * (1 - scaleDown);
            const paddingTop = scalePadding * remSize * i;

            const top = itemTops[i];
            item.style.top = `${top}px`;

            const triggerStart = itemsTop + top - firstMiddle;
            const triggerEnd =
              triggerStart + itemHeights[i] - (i === (items.current?.length || 0) - 1 ? itemHeights[i] * 0.3 : 0);

            const tl = gsap.timeline({
              scrollTrigger: {
                trigger: sectionWrapper.current,
                start: `top+=${triggerStart} center`,
                end: `top+=${triggerEnd} center`,
                scrub: 1,
                markers: false,
              },
            });

            tl.fromTo(item, { y: top / 2 }, { y: -(top - paddingTop) });

            const beforeItem = i > 0 ? items.current?.[i - 1] : null;

            if (beforeItem) {
              const opacity = 0.12 * (reverseIndex + 1);
              tl.fromTo(
                beforeItem,
                {
                  scale: 1,
                  "--tw-bg-opacity": 1,
                  transformOrigin: "center top",
                },
                { scale: 1 - scaleDown, "--tw-bg-opacity": opacity },
                "<",
              );
            }
          }
        });
      }
    });
    return () => {
      ctx.revert();
    };
  }, [absoluteItems, itemTops, itemHeights, sectionWrapper, sectionTitleHeight, pinTop, items, remSize]);

  return (
    <div
      ref={section}
      className="relative z-[15]">
      <div
        data-cards-items="true"
        className="relative">
        {cards?.map((item, index) => {
          const currentStyle = cardStyles[index % cardStyles.length];
          return (
            <div
              key={index}
              data-cards-item={index}
              className={cn("w-[min(112.625rem,100%)] mx-auto relative", index > 0 && "absolute left-0 right-0")}>
              <CardContent
                item={item}
                currentStyle={currentStyle}
                isLast={index === cards.length - 1}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function LifestyleSection({
  data,
  futures = false,
  className,
}: {
  data: PageCareerSchema;
  futures?: boolean;
  className?: string;
}) {
  return (
    <section
      className={cn(
        "relative w-full pt-[7rem] lg:pt-[20rem] lg:pb-[5rem] overflow-clip  lg:w-[min(119.125rem,100%)] mx-auto z-10",
        className,
      )}>
      <div className="flex flex-col items-start w-[min(229.25rem,100%)] mx-auto px-[2rem] lg:px-0 relative z-[5]">
        <div className="hidden lg:block container w-full relative z-10 !max-w-[190rem] lg:px-0">
          <div
            className="sticky !top-[10rem]"
            data-cards-pin="true">
            <div
              className="flex flex-col items-center"
              data-cards-title="true">
              <ChipSerif
                className="font-medium font-instrument text-black/70 text-[2.25rem] tracking-[0.025rem] mb-[1.25rem] lg:mb-[2rem]"
                text={data?.lifestyleChip || ""}
              />

              {data?.lifestyleTitle && (
                <div className="richtext w-[min(58.625rem,100%)] mb-[3rem] prose-p:text-[2rem] prose-p:text-[#1C1F22]/80">
                  <PortableText
                    value={data.lifestyleTitle}
                    components={richTextComponent}
                  />
                </div>
              )}
            </div>

            {data?.lifestyleCard && (
              <div className="w-full lg:pb-[12rem]">
                <CardWeb cards={data?.lifestyleCard} />
              </div>
            )}
          </div>
        </div>

        <div className="lg:hidden">
          <div
            className="flex flex-col"
            data-cards-title="true">
            <ChipSerif
              className="font-medium font-instrument text-black/70 text-[2.25rem] tracking-[0.025rem] mb-[1rem] text-start lg:mb-[2rem]"
              text={data?.lifestyleChip || ""}
            />

            {data?.lifestyleTitle && (
              <div className="richtext mb-[3rem] richtext-start prose-p:text-[#1C1F22]/80 prose-p:w-[min(50rem,100%)] prose-headings:text-[5rem] prose-headings:text-[#103037] prose-headings:w-[min(45rem,100%)] lg:prose-headings:w-[min(70.5rem,100%)]">
                <PortableText
                  value={data.lifestyleTitle}
                  components={richTextComponent}
                />
              </div>
            )}
          </div>

          <div className="flex flex-col gap-[1.25rem] pb-[6rem]">
            {data?.lifestyleCard?.map((item, index) => (
              <CardMobile
                key={item.title ?? `lifestyle-${index}`}
                item={item}
                index={index}
                isLast={index === (data.lifestyleCard?.length ?? 0) - 1}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
